import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface AgeGateModalProps {
  /** If true this is a re-verification (birthday milestone) */
  isReverification?: boolean;
  onComplete: () => void;
  /** Called when age < 13 during signup */
  onBlocked?: () => void;
}

const MONTH_KEYS = [
  "ageGate.january", "ageGate.february", "ageGate.march", "ageGate.april", "ageGate.may", "ageGate.june",
  "ageGate.july", "ageGate.august", "ageGate.september", "ageGate.october", "ageGate.november", "ageGate.december",
];

const LOCKOUT_KEY = "ayeris_age_lockout";
const LOCKOUT_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Check if user is currently locked out */
export const isAgeLocked = (): boolean => {
  const lockout = localStorage.getItem(LOCKOUT_KEY);
  if (!lockout) return false;
  const until = parseInt(lockout);
  if (isNaN(until)) return false;
  if (Date.now() < until) return true;
  // Expired
  localStorage.removeItem(LOCKOUT_KEY);
  return false;
};

const AgeGateModal = ({ isReverification = false, onComplete, onBlocked }: AgeGateModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1919 }, (_, i) => currentYear - i);

  const daysInMonth = month && year
    ? new Date(parseInt(year), parseInt(month), 0).getDate()
    : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Reset day if it exceeds days in selected month
  useEffect(() => {
    if (day && parseInt(day) > daysInMonth) {
      setDay("");
    }
  }, [month, year, daysInMonth, day]);

  const isValid = month && day && year;

  const handleSubmit = async () => {
    if (!isValid) return;
    setError("");
    setLoading(true);

    try {
      const birthMonth = parseInt(month);
      const birthDay = parseInt(day);
      const birthYear = parseInt(year);
      const now = new Date();

      // Calculate age
      let age = now.getFullYear() - birthYear;
      const monthDiff = now.getMonth() + 1 - birthMonth;
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDay)) {
        age--;
      }

      // Block under 13 and set lockout
      if (age < 13) {
        localStorage.setItem(LOCKOUT_KEY, String(Date.now() + LOCKOUT_DURATION_MS));
        if (onBlocked) {
          onBlocked();
        }
        setLoading(false);
        return;
      }

      const is15Plus = age >= 15;
      const is18Plus = age >= 18;
      const currentYearNow = now.getFullYear();

      // If the user's birth month hasn't passed yet this year,
      // set last_verified_year to previous year so re-verification
      // triggers at the end of their birth month this year.
      const currentMonth = now.getMonth() + 1;
      const birthMonthPassed = currentMonth > birthMonth ||
        (currentMonth === birthMonth && now.getDate() >= new Date(currentYearNow, birthMonth, 0).getDate());
      const verifiedYear = birthMonthPassed ? currentYearNow : currentYearNow - 1;

      if (user) {
        await (supabase.from("profiles") as any)
          .update({
            birth_month: birthMonth,
            is_15_plus: is15Plus,
            is_18_plus: is18Plus,
            last_verified_year: verifiedYear,
          })
          .eq("id", user.id);
      }

      // Store in localStorage for guests / sync reads
      localStorage.setItem("ayeris_birth_month", String(birthMonth));
      localStorage.setItem("ayeris_is_15_plus", String(is15Plus));
      localStorage.setItem("ayeris_is_18_plus", String(is18Plus));
      localStorage.setItem("ayeris_last_verified_year", String(verifiedYear));

      onComplete();
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const selectClass =
    "flex-1 px-3 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none";

  return (
    <div className={isReverification ? "fixed inset-0 z-[100] bg-background/95 flex items-center justify-center px-4" : "w-full flex items-center justify-center px-4"}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-5"
      >
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">{isReverification ? "🎂" : "📅"}</span>
          </div>
          <h2 className="font-display text-xl font-bold text-foreground">
            {isReverification ? t("ageGate.birthdayMilestone") : t("ageGate.dateOfBirth")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isReverification ? t("ageGate.reverifyHint") : t("ageGate.dobHint")}
          </p>
        </div>

        {/* Date selectors */}
        <div className="flex gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={selectClass}
          >
            <option value="" disabled>{t("ageGate.month")}</option>
            {MONTH_KEYS.map((key, i) => (
              <option key={key} value={String(i + 1)}>{t(key)}</option>
            ))}
          </select>

          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className={selectClass}
            style={{ maxWidth: "5rem" }}
          >
            <option value="" disabled>{t("ageGate.day")}</option>
            {days.map((d) => (
              <option key={d} value={String(d)}>{d}</option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={selectClass}
            style={{ maxWidth: "6rem" }}
          >
            <option value="" disabled>{t("ageGate.year")}</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </div>

        {error && (
          <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
        )}

        <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
          {t("ageGate.disclaimer")}
        </p>

        <button
          onClick={handleSubmit}
          disabled={!isValid || loading}
          className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("common.continue")}
        </button>
      </motion.div>
    </div>
  );
};

export default AgeGateModal;
